import React, { useEffect, useRef } from 'react';

/**
 * DynamicIframe component that automatically adjusts height based on postMessage from iframe content
 * @param {string} url - The iframe source URL
 * @param {string} className - Optional CSS class name
 * @param {object} style - Optional inline styles
 */
const DynamicIframe = ({ url, className = '', style = {} }) => {
  const iframeRef = useRef(null);
  const childOriginRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !url) return;

    try {
      // Get the origin from the iframe URL prop
      childOriginRef.current = new URL(url).origin;
    } catch (e) {
      console.warn('Invalid iframe URL:', url);
      return;
    }

    // Message handler for receiving height updates from iframe
    const handleMessage = (e) => {
      // Verify the origin matches
      if (e.origin !== childOriginRef.current) return;

      // Verify the iframe still exists and is the correct one
      const currentIframe = iframeRef.current;
      if (!currentIframe) return;

      // Verify the message came from this specific iframe's contentWindow
      // This prevents multiple iframes from the same origin from responding to each other's messages
      if (e.source !== currentIframe.contentWindow) return;

      const data = e.data || {};
      if (data.type === 'EMBED_HEIGHT') {
        currentIframe.style.height = data.height - 20 + 'px';
      }
    };

    // Add event listener
    window.addEventListener('message', handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [url]);

  return (
    <iframe
      ref={iframeRef}
      src={url}
      className={className}
      style={{
        width: '100%',
        border: 0,
        display: 'block',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
        verticalAlign: 'top',
        ...style,
      }}
      scrolling='no'
    />
  );
};

export default DynamicIframe;
